import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Alert,
  Button,
} from '@mui/material';
import {
  Payment,
  History,
  Info,
  CheckCircle,
  Error,
  Refresh,
} from '@mui/icons-material';
import SubscriptionStatus from '../components/SubscriptionStatus';
import PaymentButton from '../components/PaymentButton';
import PaywallModal from '../components/PaywallModal';
import { paymentsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatCurrency } from '../utils/helpers';

/**
 * Subscription Management Page
 */
const Subscription = () => {
  const { showError, showSuccess } = useNotification();
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [config, setConfig] = useState({
    price: 10.00,
    currency: 'USD',
    pagesPerMonth: 100,
    durationDays: 30,
    trialDays: 7
  });

  useEffect(() => {
    loadPaymentHistory();
    loadConfig();
  }, [refreshKey]);

  const loadConfig = async () => {
    try {
      const response = await paymentsApi.getConfig();
      setConfig({
        price: response.data.price || 10.00,
        currency: response.data.currency || 'USD',
        pagesPerMonth: response.data.pagesPerMonth || 100,
        durationDays: response.data.durationDays || 30,
        trialDays: response.data.trialDays || 7
      });
    } catch (error) {
      console.error('Error loading subscription config:', error);
      // Keep default values if config fails to load
    }
  };

  const loadPaymentHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await paymentsApi.getHistory({ limit: 10 });
      setPaymentHistory(response.data.payments || []);
    } catch (error) {
      console.error('Error loading payment history:', error);
      showError('Failed to load payment history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handlePaymentSuccess = (data) => {
    showSuccess('Payment successful! Your subscription has been activated.');
    setPaywallOpen(false);
    setRefreshKey((prev) => prev + 1); // Refresh subscription status
    loadPaymentHistory(); // Reload payment history
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      captured: { label: 'Success', color: 'success', icon: <CheckCircle /> },
      authorized: { label: 'Authorized', color: 'info', icon: <Info /> },
      pending: { label: 'Pending', color: 'warning', icon: <Info /> },
      failed: { label: 'Failed', color: 'error', icon: <Error /> },
      refunded: { label: 'Refunded', color: 'default', icon: <Info /> },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          <Payment sx={{ mr: 2, verticalAlign: 'middle' }} />
          Subscription & Billing
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your subscription and view payment history
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Subscription Status */}
        <Grid item xs={12} md={6}>
          <SubscriptionStatus
            key={refreshKey}
            onRenewClick={() => setPaywallOpen(true)}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
          />
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                <Alert severity="info" icon={<Info />}>
                  <Typography variant="body2">
                    Your subscription includes {config.pagesPerMonth} PDF pages per month with
                    automatic AI categorization.
                  </Typography>
                </Alert>

                <PaymentButton
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  fullWidth
                >
                  Subscribe / Renew
                </PaymentButton>

                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => setRefreshKey((prev) => prev + 1)}
                  fullWidth
                >
                  Refresh Status
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Payment History */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">
                  <History sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Payment History
                </Typography>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={loadPaymentHistory}
                >
                  Refresh
                </Button>
              </Stack>

              {loadingHistory ? (
                <Typography variant="body2" color="text.secondary">
                  Loading payment history...
                </Typography>
              ) : paymentHistory.length === 0 ? (
                <Alert severity="info">
                  No payment history yet. Your first payment will appear here.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell>Payment ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentHistory.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>
                            {formatDate(payment.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(payment.amount)} {payment.currency}
                            </Typography>
                          </TableCell>
                          <TableCell>{getStatusChip(payment.status)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {payment.paymentMethod || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(payment.subscriptionPeriodStart)}
                              <br />
                              to
                              <br />
                              {formatDate(payment.subscriptionPeriodEnd)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.7rem',
                              }}
                            >
                              {payment.razorpayPaymentId}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Pricing Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pricing Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Monthly Price
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(config.price)} {config.currency}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Pages Included
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {config.pagesPerMonth}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Billing Cycle
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {config.durationDays} Days
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Trial Period
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {config.trialDays} Days
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Paywall Modal */}
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        onSubscribe={() => {
          setPaywallOpen(false);
          // PaymentButton will handle the payment
        }}
        reason="subscription_required"
        config={config}
      />
    </Container>
  );
};

export default Subscription;

